import {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import * as React from 'react';
import type { Theme } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import Recorder from './recorder';
import { toMMSS } from '../../../utils';
import Waveform from '../../atoms/Waveform';

const useStyles = makeStyles()((theme: Theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    alignItems: 'start',
    gap: theme.spacing(1),
  },
  recordingContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing(1),
    width: '100%',
  },
  time: {
    width: '12ch',
  },
  warning: {
    fontSize: theme.typography.caption.fontSize,
    color: theme.palette.warning.main,
  },
  error: {
    fontSize: theme.typography.caption.fontSize,
    color: theme.palette.error.main,
  },
}));

export interface AudioRecorderProps {
  recording: boolean;
  mimeType?: string;
  onRecordingComplete?: (blob: Blob, startTime?: number) => void;
  minDuration?: number;
  maxDuration?: number;
  onMinDurationReached?: () => void;
  onMaxDurationReached?: () => void;
  inputDeviceId?: string;
}

const AudioRecorder: React.FC<AudioRecorderProps> = ({
  recording,
  mimeType = 'audio/webm',
  onRecordingComplete,
  minDuration = 0,
  maxDuration = Number.POSITIVE_INFINITY,
  onMinDurationReached,
  onMaxDurationReached,
  inputDeviceId,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const [timePassed, setTimePassed] = useState(0);
  const [waveformData, setWaveformData] = useState<number[]>([]);
  const [minDurationReached, setMinDurationReached] = useState(false);
  const [maxDurationReached, setMaxDurationReached] = useState(false);

  const [tooShort, setTooShort] = useState(false);
  const [tooLong, setTooLong] = useState(false);

  const recorder = useMemo(() => new Recorder(mimeType), [mimeType]);

  useEffect(() => {
    if (recording && !recorder.isRecording()) {
      setMaxDurationReached(false);
      setMinDurationReached(false);
      setTooShort(false);
      setTooLong(false);
      setWaveformData([]);
      recorder.startRecording(inputDeviceId);
    } else if (!recording && recorder.isRecording()) {
      recorder.stopRecording().then(() => {
        if (minDuration && timePassed < minDuration) {
          setWaveformData([]);
          setTooShort(true);
        } else if (onRecordingComplete) {
          const audioBlob = recorder.getRecording();
          const startTime = recorder.getRecordingStartTime();
          onRecordingComplete(audioBlob, startTime);
        }
      });
    }
  }, [recording, minDuration, timePassed, recorder, onRecordingComplete, inputDeviceId]);

  const update = useCallback(() => {
    setTimePassed(recorder.getCurrentRecordingLength());
    const newValue = recorder.getWaveformValue();
    setWaveformData((prevData) => [...prevData, newValue]);

    if (recording) {
      if (timePassed >= maxDuration && !maxDurationReached) {
        setMaxDurationReached(true);
        setTooLong(true);

        if (onMaxDurationReached) {
          onMaxDurationReached();
        }
      }

      if (timePassed < minDuration && !minDurationReached) {
        setMinDurationReached(true);
        if (onMinDurationReached) {
          onMinDurationReached();
        }
      }
    }
  }, [
    recorder,
    timePassed,
    minDuration,
    minDurationReached,
    maxDurationReached,
    maxDuration,
    recording,
    onMaxDurationReached,
    onMinDurationReached,
  ]);

  useEffect(() => {
    let animationFrameId: number;

    const animate = () => {
      if (recording) {
        update();
        animationFrameId = requestAnimationFrame(animate);
      }
    };

    if (recording) {
      animationFrameId = requestAnimationFrame(animate);
    }

    return () => cancelAnimationFrame(animationFrameId);
  }, [recording, update]);

  return (
    <div className={classes.container}>
      <div className={classes.recordingContainer}>
        <span className={classes.time}>
          {toMMSS(timePassed)}
          {maxDuration ? `/${toMMSS(maxDuration)}` : ''}
        </span>
        <Waveform audioData={waveformData} duration={timePassed} />
      </div>
      {tooShort && <span className={classes.error}>{t('audio_recorder.too_short')}</span>}
      {tooLong && <span className={classes.warning}>{t('audio_recorder.too_long')}</span>}
    </div>
  );
};

export default AudioRecorder;
